import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { environment } from '../../../../environments/environment';

type EnvironmentKind = 'production' | 'sandbox' | 'mock' | 'local' | 'other';

export interface AuthEnvironment {
  kind: EnvironmentKind;
  label: string;
  icon: string;
  title: string;
}

/**
 * Which backend this build signs in to, from environment.keycloakUrl:
 * iam.blackkonnect.io is production, sandbox.blackkonnect.io is the sandbox.
 * Shown on the auth pages so an operator knows, before signing in, whether
 * messages they send will reach real phones.
 */
export function describeAuthEnvironment(): AuthEnvironment {
  const env = environment as { keycloakUrl?: string; devMockApi?: boolean };
  let host = '';
  try {
    host = new URL(env.keycloakUrl ?? '').host.toLowerCase();
  } catch {
    host = '';
  }
  const title = host ? `Signs in through ${host}` : 'Sign-in server not configured';

  if (env.devMockApi) {
    return { kind: 'mock', label: 'Mock data', icon: 'cloud_off', title: 'Offline preview with sample data' };
  }
  if (host === 'iam.blackkonnect.io') {
    return { kind: 'production', label: 'Production', icon: 'public', title };
  }
  if (host.includes('sandbox')) {
    return { kind: 'sandbox', label: 'Sandbox', icon: 'science', title };
  }
  if (/^(localhost|127\.0\.0\.1)(:\d+)?$/.test(host)) {
    return { kind: 'local', label: 'Local', icon: 'computer', title };
  }
  return { kind: 'other', label: host || 'Unknown', icon: 'dns', title };
}

/**
 * The one layout every signed-out page uses: a centred 400px column on the
 * canvas with the theme-correct logo, the environment, an eyebrow, the page
 * heading, a one-line subtitle, the content, and a quiet footer.
 *
 *   <bk-auth-layout eyebrow="Admin console" heading="Sign in" sub="…">
 *     …form…
 *     <p authFoot>…</p>
 *   </bk-auth-layout>
 *
 * Styles are global (ViewEncapsulation.None, `bk-auth*` names) because the
 * forms project their markup into this layout and use the shared form
 * classes defined here (bk-auth-form, bk-auth-cta, bk-auth-note…).
 */
@Component({
  selector: 'bk-auth-layout',
  standalone: true,
  templateUrl: './auth-layout.component.html',
  styleUrl: './auth-layout.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class AuthLayoutComponent {
  @Input() eyebrow = 'Admin console';
  @Input({ required: true }) heading = '';
  @Input() sub = '';
  @Input() showEnvironment = true;

  @ViewChild('headingEl') private headingEl?: ElementRef<HTMLElement>;

  readonly env = describeAuthEnvironment();

  /**
   * Move focus to the heading after the page changes state (form → sent,
   * verifying → form), so screen readers announce where the user is now.
   */
  focusHeading(): void {
    setTimeout(() => this.headingEl?.nativeElement.focus());
  }
}
